import React, { useEffect, useState } from 'react';
import { Box, useTheme, Link, Grid, Badge, IconButton, Button, CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import HAppRoutes from '../../../app/features/routes/helpers/HAppRoutes';
import langDocumentFilesTable from '../langs/langDocumentFilesTable';
import AppCustomIcon from '../../../app/features/icon/AppIcon';
import AppFormatDate from '../../../app/features/format/AppFormatDate';
import styleVariables from '../../../app/styles/styleVariables';
import { KeyboardArrowDownOutlined, KeyboardArrowUpOutlined, WarningOutlined } from '@mui/icons-material';
import { BoxFundItem } from '../../investor/views/expertise/VInvestExpertisePortfolio';
import CAppPaper from '../../../app/components/CAppPaper';
import useDocumentFile from '../hooks/useDocumentFile';
import IDocumentFile from '../interfaces/IDocumentFile';
import useDocumentTypes from '../hooks/useDocumentTypes';
import langDocumentType from '../langs/langDocumentType';
import DocumentRoutes from '../DocumentRoutes';
import CDocumentDownloadErrorSnackbar from '../components/CDocumentDownloadErrorSnackbar';
import langDocumentFiles from '../langs/langDocumentFiles';
import useInvestExpertises from '../../investor/hooks/expertise/useInvestExpertises';
import { langLookup } from '../../../app/utils/langLookup';

type VDocumentFilesTableMobileV2Props = {
	documents: IDocumentFile[];
	displayColumns?: string[];
};

const nbPerView = 5;

const VDocumentFilesTableMobileV2 = (props: VDocumentFilesTableMobileV2Props) => {
	const { documents, displayColumns } = props;

	const { documentTypes } = useDocumentTypes();
	const investExpertises = useInvestExpertises();
	const navigate = useNavigate();
	const theme = useTheme();
	const { t } = useTranslation();

	const [from] = useState(0);
	const [to, setTo] = useState(nbPerView);

	const [accordionDocuments, setAccordionDocuments] = useState<IDocumentFile[] | null>(null);
	useEffect(() => {
		if (documents && documentTypes && to) {
			setAccordionDocuments(
				documents
					.filter((d, index) => index >= from && index < to)
					.map((document) => {
						const documentType = documentTypes.find((t) => t.tag === document.documentType);
						return {
							...document,
							isOpen: false,
							isDownloading: false,
							isDownloadingError: false,
							documentTypeName:
								documentType && langLookup(langDocumentType, documentType.name)
									? t(langLookup(langDocumentType, documentType.name)?.tag ?? '')
									: '',
						};
					}),
			);
		}
	}, [documents, documentTypes, t, from, to]);

	const [selectDocumentId, setSelectDocumentId] = useState<number | null>(null);
	const { document: currentDocument, download } = useDocumentFile(selectDocumentId);

	const handleDownload = (id: number) => {
		setSelectDocumentId(id);
	};

	const [downloadError, setDownloadError] = useState<'404' | 'no_current_document' | Error | null>(null);

	useEffect(() => {
		if (currentDocument) {
			setAccordionDocuments(
				(prevData) =>
					prevData?.map((x) => {
						if (x.id === currentDocument.id) {
							x.isDownloading = true;
						}
						return x;
					}) ?? null,
			);
			download()
				.then(() => {
					setAccordionDocuments(
						(prevData) =>
							prevData?.map((x) => {
								if (x.id === currentDocument.id) {
									x.isDownloading = false;
								}
								return x;
							}) ?? null,
					);
				})
				.catch((e) => {
					setDownloadError(e);
					setAccordionDocuments(
						(prevData) =>
							prevData?.map((x) => {
								if (x.id === currentDocument.id) {
									x.isDownloadingError = true;
								}
								return x;
							}) ?? null,
					);
				})
				.finally(() => {
					setSelectDocumentId(null);
				});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentDocument]);

	const handleToggleAccordionDocuments = (document: IDocumentFile) => {
		setAccordionDocuments(
			accordionDocuments?.map((x) => ({
				...x,
				isOpen: x.id === document.id ? !x.isOpen : x.isOpen,
			})) ?? null,
		);
	};

	const handleView = (id: number) => {
		navigate(HAppRoutes.getPath(DocumentRoutes.document.tag, { id: id.toString() }));
	};

	const handleDisplayMoreDocuments = () => {
		setTo((prevTo) => prevTo + nbPerView);
	};

	return (
		<Box>
			{accordionDocuments &&
				accordionDocuments.map((document) => {
					const documentType = documentTypes.find((t) => t.tag === document.documentType);
					return (
						<CAppPaper
							className="document-files-mobile"
							variant="outlined"
							key={document.id}
							sx={{
								marginBottom: '15px',
								backgroundColor: theme.palette.mode === 'dark' ? styleVariables.primaryColor : '#FFF',
								border: '1px solid white',
							}}
						>
							<Grid container spacing={3} alignItems="center">
								<Grid item xs={2}>
									<Box sx={{ textAlign: 'center' }}>
										<IconButton
											color="primary"
											onClick={() => handleView(document.id)}
											sx={{ cursor: 'pointer' }}
										>
											<Badge
												color="secondary"
												variant="dot"
												invisible={!document.isNew}
												anchorOrigin={{
													vertical: 'top',
													horizontal: 'left',
												}}
											>
												<AppCustomIcon tag={`document_format_${document.formatTag}`} />
											</Badge>
										</IconButton>
									</Box>
								</Grid>
								<Grid item xs={8}>
									<Box
										sx={{
											fontSize: '0.9rem',
											fontWeight: '400',
											color: theme.palette.primary.main,
											//maxHeight: '3rem',
											overflow: 'hidden',
										}}
									>
										{document.name}
									</Box>
								</Grid>
								<Grid item xs={2}>
									<Box sx={{ textAlign: 'center' }}>
										<IconButton
											color="primary"
											disabled={document.isDownloading || document.isDownloadingError}
											onClick={() => handleDownload(document.id)}
											sx={{ cursor: 'pointer' }}
										>
											{document.isDownloadingError ? (
												<WarningOutlined />
											) : document.isDownloading ? (
												<CircularProgress color="secondary" size={26} />
											) : (
												<AppCustomIcon tag="download" />
											)}
										</IconButton>
									</Box>
								</Grid>
							</Grid>
							<Box sx={{ marginTop: '6px' }}>
								<Link
									color="primary"
									sx={{
										position: 'relative',
										fontWeight: 600,
										fontSize: '0.875rem',
										lineHeight: '1.7',
									}}
									onClick={() => handleToggleAccordionDocuments(document)}
								>
									{t(langDocumentFiles.btn_details.tag)}
									{accordionDocuments &&
									accordionDocuments.some((i) => i.id === document.id && i.isOpen) ? (
										<KeyboardArrowUpOutlined
											sx={{
												marginTop: '0px',
												position: 'absolute',
												top: '50%',
												transform: 'translateY(-50%)',
											}}
										/>
									) : (
										<KeyboardArrowDownOutlined
											sx={{
												marginTop: '0px',
												position: 'absolute',
												top: '50%',
												transform: 'translateY(-50%)',
											}}
										/>
									)}
								</Link>
							</Box>
							{investExpertises &&
							accordionDocuments &&
							accordionDocuments.some((i) => i.id === document.id && i.isOpen) ? (
								<Box sx={{ marginTop: '6px' }}>
									<Grid container spacing={3}>
										{!displayColumns || displayColumns.includes('fund.name') ? (
											<Grid item xs={12}>
												<BoxFundItem
													label={t(langDocumentFilesTable.fund.tag)}
													value={document.fund?.name}
												/>
											</Grid>
										) : (
											''
										)}
										{!displayColumns || displayColumns.includes('fundTypeName') ? (
											<Grid item xs={6}>
												<BoxFundItem
													label={t(langDocumentFilesTable.fund_type.tag)}
													value={
														investExpertises?.find((x) => x.tag === document.fund?.typeTag)
															?.name
													}
												/>
											</Grid>
										) : (
											''
										)}
										{/* {!displayColumns || displayColumns.includes('company.name') ? (
											<Grid item xs={6}>
												<BoxFundItem
													label={t(langDocumentFilesTable.subportfolio.tag)}
													value={t(document.company?.name)}
												/>
											</Grid>
										) : (
											''
										)} */}
										{!displayColumns || displayColumns.includes('date') ? (
											<Grid item xs={6}>
												<BoxFundItem
													label={t(langDocumentFilesTable.release_date.tag)}
													value={<AppFormatDate date={document.date} />}
												/>
											</Grid>
										) : (
											''
										)}
										{!displayColumns || displayColumns.includes('documentType') ? (
											<Grid item xs={12}>
												<BoxFundItem
													label={t(langDocumentFilesTable.document_type.tag)}
													value={
														documentType && langLookup(langDocumentType, documentType.name)
															? t(
																	langLookup(langDocumentType, documentType.name)
																		?.tag ?? '',
															  )
															: documentType?.name
													}
												/>
											</Grid>
										) : (
											''
										)}
									</Grid>
								</Box>
							) : (
								''
							)}
						</CAppPaper>
					);
				})}
			{documents ? (
				<Box sx={{ textAlign: 'center' }}>
					<Button
						variant="contained"
						color="secondary"
						disabled={to > documents.length}
						onClick={() => handleDisplayMoreDocuments()}
					>
						{to > documents.length
							? t(langDocumentFiles.mobile_list_documents_text_all_documents_displayed.tag)
							: t(langDocumentFiles.mobile_list_documents_text_view_x_more_documents.tag, {
									count: nbPerView,
							  })}
					</Button>
				</Box>
			) : (
				''
			)}
			<CDocumentDownloadErrorSnackbar downloadError={downloadError} onClose={() => setDownloadError(null)} />
		</Box>
	);
};

export default VDocumentFilesTableMobileV2;
